import { makeAutoObservable, reaction } from "mobx";
import { EntityRepository } from "@/app_store/stream/entity_repository";


export interface Project {
    id: string;
    account_id: string;
    name: string;
    description: string;
    code: string;
    relative_path: string;
    open_tabs?: string[];
    active_tab_path?: string;
    open_tabs_mobile?: string[];
    active_tab_path_mobile?: string;
    open_tabs_review?: string[];
    active_tab_path_review?: string;
    open_tabs_review_mobile?: string[];
    active_tab_path_review_mobile?: string;
    selected_branch?: string;
    deployment_env?: string;
    deploy_output_scale?: number;
    deploy_output_scale_mobile?: number;
    created_at: string;
    updated_at: string | number;
    is_deleted?: boolean;
}


export class ProjectStore {
    root: any;
    repository = new EntityRepository<Project>("id");
    // The project the user last asked to have open, independent of whether the backend
    // has confirmed it yet. selectedProjectId (below) is the confirmed one and resets to
    // null on every reconnect (a fresh Session starts with no project selected) -- this is
    // the only thing that lets the reconnect reaction below know what to re-select.
    private _desiredProjectId: string | null = null;

    constructor(root: any) {
        this.root = root;
        makeAutoObservable(this, { repository: false, root: false });

        // AgentStore hands out a fresh, empty `session` on every (re)connect (a new
        // websocket connection means a new server-side Session) -- re-select so the
        // currently-open project's container comes back instead of staying empty.
        // Optional chaining: ProjectStore is constructed before AgentStore (see
        // @/app_store/index.ts), so `root.agent` doesn't exist yet on this first call.
        reaction(
            () => this.root.agent?.isConnected,
            (isConnected: boolean | undefined) => {
                if (isConnected && this._desiredProjectId) this.selectProject(this._desiredProjectId);
            }
        );
    }

    get all(): Project[] {
        return this.repository.active.map(e => e.value);
    }

    get isSelectingProject(): boolean {
        return this._desiredProjectId !== null && !this.selectedProjectId;
    }

    get selectedProjectId(): string | null {
        const projectPath: string | null | undefined = this.root.agent?.session?.project?.path;
        if (!projectPath) return null;
        let best: Project | null = null;
        for (const project of this.all) {
            const rel = project.relative_path;
            if (!rel) continue;
            if (projectPath === rel || projectPath.endsWith("/" + rel)) {
                if (!best || rel.length > best.relative_path.length) best = project;
            }
        }
        return best?.id ?? null;
    }

    get selectedEntity() {
        if (!this.selectedProjectId) return null;
        return this.repository.get(this.selectedProjectId);
    }

    get selectedProject(): Project | null {
        return this.selectedEntity?.value ?? null;
    }

    findByRelativePath(relativePath: string): Project | undefined {
        return this.all.find(p => p.relative_path === relativePath);
    }

    async selectProject(id: string) {
        this._desiredProjectId = id;

        const project = this.repository.get(id)?.value;
        if (!project || !this.root.agent.isConnected) return;
        await this.root.agent.session.select_project(project.relative_path);
    }

    closeProject() {
        this._desiredProjectId = null;
        this.root.agent.session.unselect_project();
    }

    updateCode(code: string) {
        const entity = this.selectedEntity;
        if (!entity) return;
        entity.update({ code, updated_at: Date.now() / 1000 });
    }

    setSelectedBranch(branch: string | null) {
        const entity = this.selectedEntity;
        if (!entity) return;
        entity.update({ selected_branch: branch ?? "", updated_at: Date.now() / 1000 });
    }

    setDeploymentEnv(env: string) {
        const entity = this.selectedEntity;
        if (!entity) return;
        entity.update({ deployment_env: env, updated_at: Date.now() / 1000 });
    }

    setDeployOutputScale(scale: number, mobile: boolean = false) {
        const entity = this.selectedEntity;
        if (!entity) return;
        if (mobile) {
            entity.update({ deploy_output_scale_mobile: scale, updated_at: Date.now() / 1000 });
        } else {
            entity.update({ deploy_output_scale: scale, updated_at: Date.now() / 1000 });
        }
    }

    updateOpenTabs(tabPaths: string[], activePath: string | null, mobile: boolean = false) {
        const entity = this.selectedEntity;
        if (!entity) return;
        if (mobile) {
            entity.update({ open_tabs_mobile: tabPaths, active_tab_path_mobile: activePath ?? "", updated_at: Date.now() / 1000 });
        } else {
            entity.update({ open_tabs: tabPaths, active_tab_path: activePath ?? "", updated_at: Date.now() / 1000 });
        }
    }

    updateReviewOpenTabs(tabPaths: string[], activePath: string | null, mobile: boolean = false) {
        const entity = this.selectedEntity;
        if (!entity) return;
        if (mobile) {
            entity.update({ open_tabs_review_mobile: tabPaths, active_tab_path_review_mobile: activePath ?? "", updated_at: Date.now() / 1000 });
        } else {
            entity.update({ open_tabs_review: tabPaths, active_tab_path_review: activePath ?? "", updated_at: Date.now() / 1000 });
        }
    }

    create(name: string, relativePath: string = "") {
        const accountId = this.root.account.account?.id;
        if (!accountId) return;
        const id = crypto.randomUUID();
        const entity = this.repository.addRemote({
            id,
            account_id: accountId,
            name,
            description: "",
            code: "",
            relative_path: relativePath,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        });
        entity.update(entity.remoteValue!);
        this.selectProject(id);
    }
}
